import axios from "axios";
import { APIV2 } from "../../../backend";
import { useAppContext } from "../../../hooks/context/AppContext";
import { useState, useMemo } from "react";
import useSilentIdTokenRefresh from "../../../msal/useSilentTokenRefresh";

// Common error handler
const handleError = (error: unknown, context: string) => {
  console.error(`Error ${context}:`, error);
  return null;
};

export const useTechnicalScopesApiClient = () => {
  const { authData } = useAppContext();
  const [idToken, setIdToken] = useState(null);
  const getIdTokenSilently = useSilentIdTokenRefresh(
    (token: any | null, account: any | null) => {
      setIdToken(token);
    }
  );

  const apiClient = useMemo(() => {
    const instance = axios.create({
      baseURL: APIV2,
      headers: {
        "Content-Type": "application/json",
        Authorization: idToken
          ? `Bearer ${idToken}`
          : `Bearer ${authData.idToken}`,
      },
    });

    // Add response interceptor to handle 401 errors
    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // If the error is 401 and we haven't already retried
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const { token } = await getIdTokenSilently();
            if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return instance(originalRequest);
            }
          } catch (refreshError) {
            console.error("Error refreshing token:", refreshError);
            // If token refresh fails, redirect to login or handle as needed
            // You might want to add your auth redirect logic here
            window.location.reload();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
    return instance;
  }, [idToken, authData.idToken]);

  const getRisks = async () => {
    try {
      const response = await apiClient.get("/risks");
      return response.data;
    } catch (err) {
      return handleError(err, "fetching risks");
    }
  };

  // Options for various fields
  const getNewChassisOptions = async () => {
    try {
      const response = await apiClient.get('/lookup/TS_New_Chassis');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching New Chassis options');
    }
  };

  const getNewGoodiesOptions = async () => {
    try {
      const response = await apiClient.get('/lookup/TS_New_Goodies');
      const data = response.data || [];
      const preferredOrder = ['Yes', 'No'];

    // Custom sort
    const sorted = data.sort((a:any, b:any) => {
      const valA = a?.Valuez || '';
      const valB = b?.Valuez || '';

      const indexA = preferredOrder.indexOf(valA);
      const indexB = preferredOrder.indexOf(valB);

      if (indexA === -1 && indexB === -1) {
        return valA.localeCompare(valB); // fallback alphabetical
      } else if (indexA === -1) {
        return 1;
      } else if (indexB === -1) {
        return -1;
      } else {
        return indexA - indexB;
      }
    });

    return sorted;
    } catch (err) {
      return handleError(err, 'fetching New Goodies options');
    }
  };

  const getLockedPackagingOptions = async () => {
    try {
      const response = await apiClient.get('/lookup/TS_Locked_Packaging');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching Locked Packaging options');
    }
  };
  const getLockedFormulationOptions = async () => {
    try {
      const response = await apiClient.get('/lookup/TS_Locked_Formulation');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching Locked Formulation options');
    }
  };
  const getTechnicalScopeById = async (id: any) => {
    try {
      const response = await apiClient.get(`/technicalScope/${id}`);
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching technical scope');
    }
  };
  
  const getTSFormatOptions = async (): Promise<any[]> => {
    try {
      const response = await apiClient.get('/lookup/TS_Format');
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
       // @ts-ignore
      return handleError(error, 'fetching TS format options');
    }
  };

  const getTSPackTypeOptions = async (): Promise<any[]> => {
    try {
      const response = await apiClient.get('/lookup/TS_PackType');
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
       // @ts-ignore
      return handleError(error, 'fetching TS pack type options');
    }
  };

  const createTechnicalScope = async (payload: any) => {
    try {
      const response = await apiClient.post('/technicalScope', payload);
      return response.data;
    } catch (err) {
      return handleError(err, 'creating technical scope');
    }
  };

  const updateTechnicalScope = async (payload: any, project_key: any) => {
    try {
      const response = await apiClient.put(
        `/technicalScope/${project_key}`,
        payload
      );
      return response.data;
    } catch (err) {
      return handleError(err, 'updating technical scope');
    }
  };

  return {
    getRisks,
    getLockedPackagingOptions,
    getNewChassisOptions,
    getNewGoodiesOptions,
    getLockedFormulationOptions,
    getTSFormatOptions,
    getTSPackTypeOptions,
    createTechnicalScope,
    updateTechnicalScope,
    getTechnicalScopeById
  };
}